// ─────────────────────────────────────────────────────────────────────────────
// EvalSurvival
// "How likely am I to pass each prop firm's eval?"
//
// Runs a rolling-window simulation across all available prop firm presets
// and presents the results as a leaderboard with detailed drilldowns.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import {
  survivalScore, simulateEvalFromProfile, FAILURE_LABEL,
  whatIfScorePreset, WHAT_IF_PRESETS,
  projectToPass,
} from "../../lib/evalSimulator";
import { usePropFirmPresets } from "../../hooks/usePropFirmPresets";
import { useCustomEvalPresets } from "../../hooks/useCustomEvalPresets";
import { CustomEvalModal } from "./CustomEvalModal";
import { useViewport } from "../../hooks/useViewport";

const SIZE_TIERS = [
  { id: "all",    label: "All Sizes",   filter: () => true },
  { id: "25k",    label: "$25K",        filter: p => p.account_size === 25000 },
  { id: "50k",    label: "$50K",        filter: p => p.account_size === 50000 },
  { id: "100k",   label: "$100K",       filter: p => p.account_size === 100000 },
  { id: "150k",   label: "$150K+",      filter: p => p.account_size >= 150000 },
];

export function EvalSurvival({ trades = [], accounts = [], currentAccountId, setCurrentAccountId, userId }) {
  const { presets: systemPresets, loading: systemLoading } = usePropFirmPresets();
  const { presets: customPresets, loading: customLoading, createPreset, updatePreset, deletePreset } = useCustomEvalPresets(userId);
  const [sizeFilter, setSizeFilter] = useState("50k");
  const [expandedId, setExpandedId] = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editPreset, setEditPreset] = useState(null); // null = create, object = edit
  const [simMode, setSimMode]       = useState("last30"); // "last30" | "alltime"

  const loading = systemLoading || customLoading;

  // Merge system presets + custom presets into one list.
  // Custom presets get is_custom: true so the UI can badge them.
  const allPresets = useMemo(() => [
    ...systemPresets,
    ...customPresets.map(p => ({ ...p, is_custom: true })),
  ], [systemPresets, customPresets]);

  // EvalSurvival manages its own account selection independently from the
  // global currentAccountId. It defaults to the account with the most trading
  // history, since sparse accounts produce misleading simulation results.
  // The user can still switch accounts via the in-page selector.
  const bestAccountId = useMemo(() => {
    const activeAccounts = accounts.filter(a => !a.is_blown);
    if (!activeAccounts.length) return currentAccountId;
    // Count unique trading days per non-blown account
    const daysByAccount = new Map();
    for (const t of trades) {
      const id = t.account_id;
      if (!activeAccounts.find(a => a.id === id)) continue;
      if (!id) continue;
      const d = String(t.trade_date || "").slice(0, 10);
      if (!d) continue;
      if (!daysByAccount.has(id)) daysByAccount.set(id, new Set());
      daysByAccount.get(id).add(d);
    }
    // Pick the account with the most unique trading days
    let best = currentAccountId;
    let bestDays = daysByAccount.get(currentAccountId)?.size || 0;
    for (const [id, days] of daysByAccount.entries()) {
      if (days.size > bestDays) {
        bestDays = days.size;
        best = id;
      }
    }
    return best;
  }, [accounts, trades, currentAccountId]);

  const [localAccountId, setLocalAccountId] = useState(null);

  // Resolve which account to actually use:
  // - If user has manually picked one (localAccountId), use that
  // - Otherwise default to the account with most history
  const activeAccountId = localAccountId ?? bestAccountId;

  const accountTrades = useMemo(() => {
    if (!activeAccountId) return trades;
    return trades.filter(t => t.account_id === activeAccountId);
  }, [trades, activeAccountId]);

  const filteredPresets = useMemo(() => {
    const tier = SIZE_TIERS.find(t => t.id === sizeFilter) || SIZE_TIERS[0];
    // Custom presets: always show regardless of size filter (user created them intentionally)
    // System presets: filter by account_size
    return allPresets.filter(p => p.is_custom || tier.filter(p));
  }, [allPresets, sizeFilter]);

  const results = useMemo(() => {
    if (!accountTrades.length || !filteredPresets.length) return [];
    return filteredPresets.map(preset => {
      const score = simulateEvalFromProfile(accountTrades, preset, simMode);
      return { preset, score };
    }).sort((a, b) => {
      if (b.score.score_pct !== a.score.score_pct) return b.score.score_pct - a.score.score_pct;
      return a.score.failed - b.score.failed;
    });
  }, [accountTrades, filteredPresets, simMode]);

  const bestFit = results.find(r => r.score.passed > 0);

  const aggregateFailures = useMemo(() => {
    const counts = { daily_loss: 0, trailing_drawdown: 0, static_drawdown: 0, consistency: 0 };
    let totalFailures = 0;
    let totalIncomplete = 0;
    for (const r of results) {
      for (const [reason, count] of Object.entries(r.score.failure_counts || {})) {
        counts[reason] = (counts[reason] || 0) + count;
        totalFailures += count;
      }
      totalIncomplete += r.score.incomplete || 0;
    }
    return { counts, totalFailures, totalIncomplete };
  }, [results]);

  const allCloseCalls = useMemo(() => {
    const calls = [];
    for (const r of results) {
      for (const cc of (r.score.closest_calls || [])) {
        calls.push({ ...cc, firm: r.preset });
      }
    }
    return calls.sort((a, b) => a.margin - b.margin).slice(0, 8);
  }, [results]);

  // Trading profile stats (across all account trades)
  const tradingProfile = useMemo(() => {
    if (!accountTrades.length) return null;
    const byDay = new Map();
    for (const t of accountTrades) {
      const d = String(t.trade_date || "").slice(0, 10);
      if (!d) continue;
      const net = (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
      byDay.set(d, (byDay.get(d) || 0) + net);
    }
    const daily = [...byDay.values()];
    if (!daily.length) return null;
    const winning = daily.filter(v => v > 0);
    const losing  = daily.filter(v => v < 0);
    return {
      avg_daily:      daily.reduce((s, v) => s + v, 0) / daily.length,
      win_rate:       winning.length / daily.length,
      avg_win:        winning.length ? winning.reduce((s, v) => s + v, 0) / winning.length : 0,
      avg_loss:       losing.length  ? losing.reduce((s, v) => s + v, 0)  / losing.length  : 0,
      best_day:       Math.max(...daily),
      worst_day:      Math.min(...daily),
      total_days:     daily.length,
      total_trades:   accountTrades.length,
    };
  }, [accountTrades]);

  if (loading) {
    return <div style={{ padding: 24, color: C.muted, textAlign: "center" }}>Loading prop firm presets…</div>;
  }

  if (!accountTrades.length) {
    return (
      <div style={{ padding: "20px 24px 60px", maxWidth: 1100, margin: "0 auto" }}>
        <Header
          accounts={accounts}
          currentAccountId={activeAccountId}
          setCurrentAccountId={setLocalAccountId}
          tradesCount={0}
        />
        <Card>
          <SectionTitle>Eval Survival</SectionTitle>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6 }}>
            No trades found in this account yet. Once you import a CSV or log some trades,
            we can simulate your performance against {allPresets.length} prop firm rulesets.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px 60px", maxWidth: 1100, margin: "0 auto" }}>
      <Header
        accounts={accounts}
        currentAccountId={activeAccountId}
        setCurrentAccountId={setLocalAccountId}
        tradesCount={accountTrades.length}
      />

      <SizeFilterRow
        sizeFilter={sizeFilter}
        setSizeFilter={setSizeFilter}
        onAddCustom={() => { setEditPreset(null); setModalOpen(true); }}
      />

      {/* ── Simulation mode toggle ──────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px", marginBottom: 16,
        background: "#0d1520", border: `1px solid #1a2a3a`, borderRadius: 8,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#e6edf3", marginBottom: 3 }}>
            Simulation basis
          </div>
          <div style={{ fontSize: 12, color: "#8b949e" }}>
            {simMode === "last30"
              ? "Using your trades from the last 30 days — reflects your current form."
              : "Using your full trading history — reflects your all-time average pattern."}
          </div>
        </div>
        <div style={{
          display: "inline-flex", background: "#040810",
          border: "1px solid #1a2a3a", borderRadius: 6, padding: 3, flexShrink: 0, marginLeft: 16,
        }}>
          {[
            { id: "last30", label: "Last 30 days" },
            { id: "alltime", label: "All-time avg" },
          ].map(opt => {
            const active = simMode === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setSimMode(opt.id)}
                style={{
                  padding: "7px 16px", borderRadius: 4, border: "none",
                  fontSize: 12, fontWeight: active ? 700 : 400, cursor: "pointer",
                  background: active ? "#00d4ff" : "transparent",
                  color: active ? "#080d14" : "#8b949e",
                  transition: "all 0.15s",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {tradingProfile && <TradingProfileCard profile={tradingProfile} />}

      {bestFit && <BestFitCard bestFit={bestFit} totalTrades={accountTrades.length} />}

      <Leaderboard
        results={results}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        accountTrades={accountTrades}
        onEditCustom={p => { setEditPreset(p); setModalOpen(true); }}
        onDeleteCustom={deletePreset}
      />

      {(aggregateFailures.totalFailures > 0 || aggregateFailures.totalIncomplete > 0) && (
        <FailureBreakdown breakdown={aggregateFailures} />
      )}

      {allCloseCalls.length > 0 && <ClosestCalls calls={allCloseCalls} />}

      <Disclaimer />

      {/* ── Modal ──────────────────────────────────────────────────── */}
      <CustomEvalModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditPreset(null); }}
        editPreset={editPreset}
        onSave={async (fields) => {
          if (editPreset) {
            await updatePreset(editPreset.id, fields);
          } else {
            await createPreset(fields);
          }
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRADING PROFILE CARD
// Shows your key stats so users have context for what the simulation means.
// ─────────────────────────────────────────────────────────────────────────────

function TradingProfileCard({ profile }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 10 }}>
        Your Trading Profile
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}>
        <ProfileStat label="Avg Daily P&L" value={fmt$(profile.avg_daily)} color={profile.avg_daily >= 0 ? C.green : C.red} />
        <ProfileStat label="Win Rate (days)" value={`${Math.round(profile.win_rate * 100)}%`} color={profile.win_rate >= 0.5 ? C.green : C.yellow} />
        <ProfileStat label="Avg Win Day" value={fmt$(profile.avg_win)} color={C.green} />
        <ProfileStat label="Avg Loss Day" value={fmt$(profile.avg_loss)} color={C.red} />
        <ProfileStat label="Best Day" value={fmt$(profile.best_day)} color={C.green} />
        <ProfileStat label="Worst Day" value={fmt$(profile.worst_day)} color={C.red} />
        <ProfileStat label="Days Traded" value={profile.total_days} color={C.text} />
        <ProfileStat label="Total Trades" value={profile.total_trades} color={C.text} />
      </div>
    </Card>
  );
}

function ProfileStat({ label, value, color }) {
  return (
    <div style={{ padding: "10px 12px", background: "rgba(0,0,0,0.2)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color, fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HEADER, SIZE FILTER (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

function Header({ accounts, currentAccountId, setCurrentAccountId, tradesCount }) {
  const currentAccount = accounts.find(a => a.id === currentAccountId) || accounts[0];

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: C.text, margin: 0 }}>Eval Survival</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: "6px 0 0" }}>
            Test your trading history against each prop firm's actual ruleset. See where you'd thrive — and where you'd blow the account.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12, color: C.muted }}>{tradesCount} trades analyzed</div>
          {currentAccount && (
            <div style={{ fontSize: 12, color: C.sub, marginTop: 2, fontWeight: 600 }}>
              {currentAccount.name}{currentAccount.account_type ? ` — ${currentAccount.account_type.toUpperCase()}` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Account selector — always show when multiple accounts exist */}
      {accounts.length > 1 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
            Account
          </div>
          <select
            value={currentAccountId || ""}
            onChange={e => setCurrentAccountId(e.target.value || null)}
            style={{
              background: "#1a1d2e", border: `1px solid ${C.border}`, color: C.text,
              padding: "8px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", maxWidth: 320,
            }}
          >
            {accounts.filter(a => !a.is_blown).map(a => (
              <option key={a.id} value={a.id}>
                {a.name} — {a.account_type ? a.account_type.toUpperCase() : "LIVE"} ({fmt$(a.starting_balance || 0)})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Single account — still show a clear label so user knows what's being analyzed */}
      {accounts.length === 1 && currentAccount && (
        <div style={{
          marginTop: 12,
          padding: "8px 12px",
          background: "rgba(0,0,0,0.2)",
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
        }}>
          <span style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>Analyzing</span>
          <span style={{ color: C.text, fontWeight: 700 }}>{currentAccount.name}</span>
          <span style={{ color: C.muted }}>—</span>
          <span style={{ color: C.sub }}>{fmt$(currentAccount.starting_balance || 0)}</span>
        </div>
      )}
    </div>
  );
}

function SizeFilterRow({ sizeFilter, setSizeFilter, onAddCustom }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {SIZE_TIERS.map(tier => (
          <button
            key={tier.id}
            onClick={() => setSizeFilter(tier.id)}
            style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: `1px solid ${sizeFilter === tier.id ? C.blue : C.border}`,
              background: sizeFilter === tier.id ? C.blue + "22" : "transparent",
              color: sizeFilter === tier.id ? C.blue : C.muted,
              cursor: "pointer",
            }}
          >
            {tier.label}
          </button>
        ))}
      </div>
      <button
        onClick={onAddCustom}
        style={{
          padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
          border: `1px solid ${C.purple}`,
          background: C.purple + "22",
          color: C.purple,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        + Add Custom Eval
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BEST FIT CARD — expanded with fit reasoning
// ─────────────────────────────────────────────────────────────────────────────

function BestFitCard({ bestFit, totalTrades }) {
  const { preset, score } = bestFit;

  // Build 1-2 sentences explaining why this firm fits this trader
  const reasons = [];
  const proj = score.runs.length > 0
    ? score.runs.reduce((s, r) => s + r.net_profit, 0) / score.runs.length
    : 0;
  if (preset.max_daily_loss == null) reasons.push("no daily loss limit suits your trading style");
  if (preset.drawdown_type === "static") reasons.push("static drawdown gives you more room to recover from down days");
  if (proj > 0 && preset.profit_target) {
    const pct = Math.round((proj / preset.profit_target) * 100);
    if (pct >= 70) reasons.push(`you average ${pct}% of the profit target per simulation run`);
  }
  if (score.passed > 1) reasons.push(`you passed ${score.passed} of ${score.total} simulated runs`);

  return (
    <Card style={{ marginBottom: 16, background: C.green + "0a", border: `1px solid ${C.green}44` }}>
      <div style={{ fontSize: 10, color: C.green, letterSpacing: "0.1em", fontWeight: 700 }}>
        YOUR BEST FIT
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>
          {preset.firm_name} — {preset.plan_name}
        </div>
        <div style={{ fontSize: 32, fontWeight: 900, color: C.green, fontFamily: "monospace" }}>
          {score.total === 1 ? "PASS" : `${Math.round(score.score_pct)}%`}
        </div>
      </div>
      <div style={{ fontSize: 13, color: C.sub, marginTop: 6, lineHeight: 1.55 }}>
        {score.total === 1
          ? "Based on your selected trading period, you would have passed this eval."
          : <>Across {score.total} simulated evals, you would have passed{" "}
              <strong style={{ color: C.green }}>{score.passed}</strong>.</>}
        {reasons.length > 0 && (
          <span style={{ color: C.muted }}> This firm fits your style because {reasons[0]}.</span>
        )}
      </div>
      {preset.source_url && (
        <a href={preset.source_url} target="_blank" rel="noreferrer"
          style={{ display: "inline-block", marginTop: 10, fontSize: 11, color: C.blue }}>
          Official rules ↗
        </a>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LEADERBOARD
// ─────────────────────────────────────────────────────────────────────────────

function Leaderboard({ results, expandedId, setExpandedId, accountTrades, onEditCustom, onDeleteCustom }) {
  if (results.length === 0) {
    return (
      <Card>
        <SectionTitle>Leaderboard</SectionTitle>
        <div style={{ color: C.muted, fontSize: 13, padding: 12 }}>No prop firms match the selected filter.</div>
      </Card>
    );
  }
  return (
    <Card style={{ padding: 0, marginBottom: 16, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px 8px" }}>
        <SectionTitle>Leaderboard</SectionTitle>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Sorted by pass rate. Click any row for details.</div>
      </div>
      <div>
        {results.map((r, i) => (
          <LeaderboardRow
            key={r.preset.id}
            rank={i + 1}
            result={r}
            expanded={expandedId === r.preset.id}
            onToggle={() => setExpandedId(expandedId === r.preset.id ? null : r.preset.id)}
            accountTrades={accountTrades}
            onEditCustom={onEditCustom}
            onDeleteCustom={onDeleteCustom}
          />
        ))}
      </div>
    </Card>
  );
}

function LeaderboardRow({ rank, result, expanded, onToggle, accountTrades, onEditCustom, onDeleteCustom }) {
  const { isMobile } = useViewport();
  const { preset, score } = result;
  const pct = Math.round(score.score_pct);
  const barColor = pct >= 70 ? C.green : pct >= 40 ? C.yellow : pct >= 15 ? C.red : C.muted;
  const isSingle = score.total === 1;

  // Same "avg progress toward target" calc used in FirmDetail's
  // "How close you got" section — pulled up here so the collapsed
  // row can show it too instead of a bare "Incomplete".
  const avgProgressPct = score.runs.length > 0 && preset.profit_target
    ? score.runs.reduce((s, r) => s + Math.min(100, Math.max(0, (r.net_profit / Number(preset.profit_target)) * 100)), 0) / score.runs.length
    : null;

  const incompleteLabel = avgProgressPct != null
    ? `${Math.round(avgProgressPct)}%`
    : (isSingle ? "Incomplete" : `${score.incomplete} incomplete`);

  const status = score.passed > 0
    ? (isSingle ? "Pass ✓" : `${score.passed}/${score.total} ✓`)
    : score.failed > 0
    ? (isSingle ? "Fail ✗" : `${score.failed} failures`)
    : incompleteLabel;

  return (
    <div style={{ borderTop: `1px solid ${C.border}` }}>
      <div
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "32px 1fr 16px" : "32px 1fr 240px 90px 80px 16px",
          gap: 12, alignItems: "center",
          padding: isMobile ? "12px 14px" : "12px 18px",
          cursor: "pointer", transition: "background 0.1s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <div style={{ color: C.muted, fontSize: 11, fontFamily: "monospace" }}>#{rank}</div>
        <div>
          <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: C.text }}>
            {preset.firm_name}{" "}
            <span style={{ color: C.sub, fontWeight: 500 }}>— {preset.plan_name}</span>
            {preset.is_custom && (
              <span style={{
                marginLeft: 8, fontSize: 9, fontWeight: 700,
                padding: "2px 6px", borderRadius: 4,
                background: C.purple + "22", border: `1px solid ${C.purple}44`,
                color: C.purple, verticalAlign: "middle", letterSpacing: "0.05em",
              }}>
                CUSTOM
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Target {fmt$(Number(preset.profit_target || 0))} • DD {fmt$(Number(preset.max_drawdown))}
            {preset.max_daily_loss ? ` • DLL ${fmt$(Number(preset.max_daily_loss))} ${preset.daily_loss_type}` : " • no DLL"}
          </div>
          {isMobile && (
            <div style={{ marginTop: 6 }}>
              <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", marginBottom: 3 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{pct}% · {status}</div>
            </div>
          )}
        </div>
        {!isMobile && (
          <>
            <div>
              <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden", marginBottom: 4 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{status}</div>
            </div>
            <div style={{ textAlign: "right", fontSize: 18, fontWeight: 800, color: barColor, fontFamily: "monospace" }}>
              {isSingle
                ? (score.passed ? "PASS" : score.failed ? "FAIL" : "—")
                : `${pct}%`}
            </div>
          </>
        )}
        {score.total > 1 && <div style={{ fontSize: 10, color: C.muted, textAlign: "right", fontFamily: "monospace" }}>{score.total} runs</div>}
        <div style={{ color: C.muted, fontSize: 10 }}>{expanded ? "▾" : "▸"}</div>
      </div>
      {expanded && <FirmDetail result={result} accountTrades={accountTrades} onEditCustom={onEditCustom} onDeleteCustom={onDeleteCustom} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIRM DETAIL — rich drilldown
// ─────────────────────────────────────────────────────────────────────────────

function FirmDetail({ result, accountTrades, onEditCustom, onDeleteCustom }) {
  const { preset, score } = result;
  const [whatIfPresetId, setWhatIfPresetId] = useState(null);
  const [deletingCustom, setDeletingCustom] = useState(false);

  // What-if result (computed on demand when user selects a preset)
  const whatIfResult = useMemo(() => {
    if (!whatIfPresetId) return null;
    const wp = WHAT_IF_PRESETS.find(p => p.id === whatIfPresetId);
    if (!wp) return null;
    return whatIfScorePreset(accountTrades, preset, wp);
  }, [whatIfPresetId, accountTrades, preset]);

  // Progress projection
  const projection = useMemo(() => projectToPass(accountTrades, preset), [accountTrades, preset]);

  // How close did we get on avg?
  const avgProgressPct = score.runs.length > 0 && preset.profit_target
    ? score.runs.reduce((s, r) => s + Math.min(100, Math.max(0, (r.net_profit / Number(preset.profit_target)) * 100)), 0) / score.runs.length
    : null;

  const bestRun = score.runs.length > 0
    ? score.runs.reduce((best, r) => r.net_profit > best.net_profit ? r : best, score.runs[0])
    : null;

  return (
    <div style={{ background: "rgba(0,0,0,0.2)", padding: "16px 18px", borderTop: `1px solid ${C.border}` }}>

      {/* ── Stat pills ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 16 }}>
        <StatPill label="Passed"     value={score.passed}     color={C.green} />
        <StatPill label="Failed"     value={score.failed}     color={C.red} />
        <StatPill label="Incomplete" value={score.incomplete}  color={C.muted} />
        <StatPill label="Total runs" value={score.total}       color={C.text} />
      </div>

      {/* ── How close you got ─────────────────────────────────── */}
      {avgProgressPct != null && (
        <DetailSection title="How close you got">
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, marginBottom: 4 }}>
              <span>Avg progress toward target</span>
              <span style={{ fontFamily: "monospace", color: avgProgressPct >= 70 ? C.green : avgProgressPct >= 40 ? C.yellow : C.red }}>
                {Math.round(avgProgressPct)}%
              </span>
            </div>
            <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
              <div style={{
                width: `${Math.min(100, avgProgressPct)}%`, height: "100%",
                background: avgProgressPct >= 70 ? C.green : avgProgressPct >= 40 ? C.yellow : C.red,
                transition: "width 0.4s",
              }} />
            </div>
          </div>
          {bestRun && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              Best run:{" "}
              <span style={{ color: C.text }}>
                {fmt$(bestRun.net_profit)} profit in {bestRun.days_traded} trading days
                {bestRun.passed ? <span style={{ color: C.green }}> ✓ passed</span> : " (didn't pass)"}
              </span>
            </div>
          )}
          {score.runs.filter(r => r.consistency_warning).length > 0 && (
            <div style={{ fontSize: 11, color: C.yellow, marginTop: 4 }}>
              ⚠ {score.runs.filter(r => r.consistency_warning).length} run(s) hit the consistency rule
            </div>
          )}
        </DetailSection>
      )}

      {/* ── Gap analysis ───────────────────────────────────────── */}
      {projection && (
        <GapAnalysis projection={projection} preset={preset} />
      )}

      {/* ── Consistency check ─────────────────────────────────── */}
      {projection && preset.consistency_enabled && (
        <ConsistencyCheck projection={projection} preset={preset} />
      )}

      {/* ── What-if scenarios ─────────────────────────────────── */}
      <WhatIfPanel
        preset={preset}
        score={score}
        whatIfPresetId={whatIfPresetId}
        setWhatIfPresetId={setWhatIfPresetId}
        whatIfResult={whatIfResult}
      />

      {/* ── Progress projection ────────────────────────────────── */}
      {projection && (
        <ProgressProjection projection={projection} preset={preset} />
      )}

      {/* ── Why you failed ──────────────────────────────────────── */}
      {Object.keys(score.failure_counts || {}).length > 0 && (
        <DetailSection title="Why you failed">
          {Object.entries(score.failure_counts).map(([reason, count]) => (
            <div key={reason} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
              <span style={{ color: C.text }}>{FAILURE_LABEL[reason] || reason}</span>
              <span style={{ color: C.red, fontFamily: "monospace" }}>{count}</span>
            </div>
          ))}
        </DetailSection>
      )}

      {/* ── Run timeline ────────────────────────────────────────── */}
      <RunTimeline runs={score.runs} />

      {/* ── Firm notes ──────────────────────────────────────────── */}
      {preset.notes && (
        <div style={{ marginTop: 12, padding: 10, background: C.blue + "11", borderRadius: 6, fontSize: 11, color: C.sub, lineHeight: 1.5 }}>
          <strong style={{ color: C.blue }}>Note: </strong>{preset.notes}
          {preset.source_url && (
            <> · <a href={preset.source_url} target="_blank" rel="noreferrer" style={{ color: C.blue }}>official rules ↗</a></>
          )}
        </div>
      )}

      {/* ── Custom eval actions ─────────────────────────────────── */}
      {preset.is_custom && (
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          marginTop: 16, paddingTop: 14,
          borderTop: `1px solid ${C.border}`,
        }}>
          <button
            onClick={() => onEditCustom?.(preset)}
            style={{
              padding: "7px 16px", fontSize: 12, fontWeight: 600,
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 6, color: C.muted, cursor: "pointer",
            }}
          >
            Edit eval
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Delete this custom eval? This can't be undone.")) return;
              setDeletingCustom(true);
              await onDeleteCustom?.(preset.id);
            }}
            disabled={deletingCustom}
            style={{
              padding: "7px 16px", fontSize: 12, fontWeight: 600,
              background: "transparent", border: `1px solid ${C.red}55`,
              borderRadius: 6, color: C.red, cursor: "pointer",
              opacity: deletingCustom ? 0.5 : 1,
            }}
          >
            {deletingCustom ? "Deleting…" : "Delete eval"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GAP ANALYSIS
// ─────────────────────────────────────────────────────────────────────────────

function GapAnalysis({ projection, preset }) {
  const target = Number(preset.profit_target || 0);
  if (!target) return null;

  const avgDaily = projection.avg_daily_pnl;
  const avgProgress = projection.avg_progress_pct;
  const remaining = target * (1 - Math.min(1, avgProgress / 100));

  // Days needed at current avg to cover remaining gap
  const daysNeeded = avgDaily > 0 ? Math.ceil(remaining / avgDaily) : null;

  // Needed avg to pass in a given horizon (20 trading days)
  const horizonDays = 20;
  const neededAvg = remaining / horizonDays;

  const gap = neededAvg - Math.max(0, avgDaily);
  const onTrack = avgDaily >= neededAvg;

  return (
    <DetailSection title="Gap Analysis">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <MiniStat
          label="Your avg daily P&L"
          value={fmt$(avgDaily)}
          color={avgDaily >= 0 ? C.green : C.red}
        />
        <MiniStat
          label={`Needed avg (${horizonDays}-day window)`}
          value={fmt$(neededAvg)}
          color={onTrack ? C.green : C.yellow}
        />
      </div>

      {onTrack ? (
        <div style={{ fontSize: 12, color: C.green, lineHeight: 1.5 }}>
          ✓ You're on pace. At your current average, you should hit the target in roughly{" "}
          <strong>{daysNeeded ?? "?"} trading days</strong>.
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          To pass in {horizonDays} trading days you'd need to average{" "}
          <strong style={{ color: C.yellow }}>{fmt$(neededAvg)}/day</strong> — that's{" "}
          <strong style={{ color: C.yellow }}>{fmt$(gap)} more</strong> than your current average.
          {daysNeeded != null && (
            <> At your current pace it would take roughly <strong>{daysNeeded} more trading days</strong>.</>
          )}
          {avgDaily <= 0 && (
            <span style={{ color: C.red }}> Your average daily P&L is negative — focus on consistency before targeting this eval.</span>
          )}
        </div>
      )}
    </DetailSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSISTENCY CHECK
// ─────────────────────────────────────────────────────────────────────────────

function ConsistencyCheck({ projection, preset }) {
  const limit = Number(preset.consistency_max_pct) * 100;
  const bestDayPct = projection.best_day_pct;
  const ok = bestDayPct <= limit;

  return (
    <DetailSection title="Consistency Rule">
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
        {preset.firm_name} caps any single day at{" "}
        <strong style={{ color: C.text }}>{Math.round(limit)}%</strong> of total profit.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
            <span style={{ color: C.muted }}>Your best day as % of target</span>
            <span style={{ color: ok ? C.green : C.red, fontFamily: "monospace" }}>
              {Math.round(bestDayPct)}% {ok ? "✓" : "✗"}
            </span>
          </div>
          <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              width: `${Math.min(100, bestDayPct)}%`, height: "100%",
              background: ok ? C.green : C.red,
            }} />
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: ok ? C.green : C.red, lineHeight: 1.5 }}>
        {ok
          ? `Your best single day (${fmt$(projection.best_day)}) is within the limit.`
          : `Your best single day (${fmt$(projection.best_day)}) would exceed the consistency cap. You'd need to spread profits more evenly across days.`
        }
      </div>
    </DetailSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT-IF PANEL
// ─────────────────────────────────────────────────────────────────────────────

function WhatIfPanel({ preset, score, whatIfPresetId, setWhatIfPresetId, whatIfResult }) {
  const baselinePct = Math.round(score.score_pct);

  return (
    <DetailSection title="What-If Scenarios">
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
        How would your pass rate change if your trading were different? Select a scenario to find out.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {WHAT_IF_PRESETS.map(wp => (
          <button
            key={wp.id}
            onClick={() => setWhatIfPresetId(whatIfPresetId === wp.id ? null : wp.id)}
            style={{
              padding: "5px 12px",
              fontSize: 11, fontWeight: 600,
              borderRadius: 6,
              border: `1px solid ${whatIfPresetId === wp.id ? C.blue : C.border}`,
              background: whatIfPresetId === wp.id ? C.blue + "22" : "rgba(0,0,0,0.2)",
              color: whatIfPresetId === wp.id ? C.blue : C.muted,
              cursor: "pointer",
            }}
          >
            {wp.label}
          </button>
        ))}
      </div>

      {whatIfResult && whatIfPresetId && (() => {
        const wp = WHAT_IF_PRESETS.find(p => p.id === whatIfPresetId);
        const newPct = Math.round(whatIfResult.score_pct);
        const delta = newPct - baselinePct;
        const deltaColor = delta > 0 ? C.green : delta < 0 ? C.red : C.muted;

        return (
          <div style={{ padding: "12px 14px", background: "rgba(0,0,0,0.3)", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: newPct >= baselinePct ? C.green : C.red, fontFamily: "monospace" }}>
                {newPct}%
              </div>
              <div style={{ fontSize: 13, color: deltaColor, fontWeight: 700 }}>
                {delta > 0 ? `+${delta}` : delta}% vs baseline
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>
                ({whatIfResult.passed} passed, {whatIfResult.failed} failed, {whatIfResult.incomplete} incomplete)
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
              {wp?.description}
            </div>
          </div>
        );
      })()}
    </DetailSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESS PROJECTION
// ─────────────────────────────────────────────────────────────────────────────

function ProgressProjection({ projection, preset }) {
  const { days_to_pass, drawdown_risk, drawdown_days, avg_daily_pnl, on_track, win_rate } = projection;
  if (!preset.profit_target) return null;

  return (
    <DetailSection title="Progress Projection">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        <MiniStat label="Daily P&L avg"   value={fmt$(avg_daily_pnl)}         color={avg_daily_pnl >= 0 ? C.green : C.red} />
        <MiniStat label="Day win rate"    value={`${Math.round(win_rate * 100)}%`} color={win_rate >= 0.5 ? C.green : C.yellow} />
        <MiniStat label="Est. days left"  value={days_to_pass != null ? `~${days_to_pass}` : "—"} color={on_track ? C.green : C.yellow} />
      </div>

      {days_to_pass != null && avg_daily_pnl > 0 && (
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>
          At your current average of{" "}
          <strong style={{ color: C.text }}>{fmt$(avg_daily_pnl)}/day</strong>, you'd need roughly{" "}
          <strong style={{ color: on_track ? C.green : C.yellow }}>
            {days_to_pass} more trading {days_to_pass === 1 ? "day" : "days"}
          </strong>{" "}
          to hit {preset.firm_name}'s{" "}
          <strong style={{ color: C.text }}>{fmt$(Number(preset.profit_target))}</strong> target.
          {preset.min_trading_days > 0 && days_to_pass <= preset.min_trading_days && (
            <span style={{ color: C.muted }}>
              {" "}(The minimum trading days requirement of {preset.min_trading_days} days would be the actual constraint.)
            </span>
          )}
        </div>
      )}

      {avg_daily_pnl <= 0 && (
        <div style={{ fontSize: 12, color: C.red, lineHeight: 1.5, marginBottom: 8 }}>
          Your average daily P&L is negative or flat. Focus on consistency and reducing losing days before targeting this eval.
        </div>
      )}

      {drawdown_risk && drawdown_days != null && (
        <div style={{ padding: "8px 12px", background: C.red + "11", border: `1px solid ${C.red}33`, borderRadius: 6, fontSize: 11, color: C.red, lineHeight: 1.5 }}>
          ⚠ <strong>Drawdown risk:</strong> Based on your average losing day, you could breach the{" "}
          {fmt$(Number(preset.max_drawdown))} drawdown limit after roughly{" "}
          <strong>{drawdown_days} consecutive losing days</strong>. This eval may require tighter risk management per session.
        </div>
      )}

      {on_track && !drawdown_risk && (
        <div style={{ padding: "8px 12px", background: C.green + "11", border: `1px solid ${C.green}33`, borderRadius: 6, fontSize: 11, color: C.green, lineHeight: 1.5 }}>
          ✓ Your pace and drawdown profile are consistent with passing this eval.
        </div>
      )}
    </DetailSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function DetailSection({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontWeight: 600 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ padding: 10, background: "rgba(0,0,0,0.3)", borderRadius: 6, textAlign: "center" }}>
      <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: "monospace", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ padding: "8px 10px", background: "rgba(0,0,0,0.2)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color, fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}

function RunTimeline({ runs }) {
  if (!runs || runs.length === 0) return null;
  return (
    <DetailSection title="Run-by-run timeline (oldest start → newest)">
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {runs.map((r, i) => {
          const color = r.passed ? C.green : r.failed ? C.red : C.muted;
          const bg    = r.passed ? C.green + "11" : r.failed ? C.red + "11" : "rgba(255,255,255,0.04)";
          const symbol = r.passed ? "✓" : r.failed ? "✗" : "◌";
          const summary = r.passed
            ? `passed • ${fmt$(r.net_profit)} in ${r.days_traded} days`
            : r.failed
            ? `failed ${r.failure_date} • ${FAILURE_LABEL[r.failure_reason] || r.failure_reason}`
            : `incomplete • ${fmt$(r.net_profit)} (${r.days_traded} days, target not hit)`;
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "100px 18px 1fr",
              alignItems: "center", gap: 10,
              padding: "5px 10px", background: bg, borderRadius: 4, fontSize: 11,
            }}>
              <span style={{ color: C.muted, fontFamily: "monospace" }}>{r.start_date}</span>
              <span style={{ color, fontWeight: 700, textAlign: "center" }}>{symbol}</span>
              <span style={{ color: C.sub }}>{summary}</span>
            </div>
          );
        })}
      </div>
    </DetailSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT HOLDS YOU BACK + CLOSEST CALLS (unchanged logic, minor style cleanup)
// ─────────────────────────────────────────────────────────────────────────────

function FailureBreakdown({ breakdown }) {
  const total = breakdown.totalFailures + breakdown.totalIncomplete;
  const reasonLabels = {
    daily_loss: "Daily Loss",
    trailing_drawdown: "Trailing DD",
    static_drawdown: "Static DD",
    consistency: "Consistency",
  };
  const failureRows = Object.entries(breakdown.counts)
    .filter(([_, c]) => c > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>What Holds You Back</SectionTitle>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Across all simulated runs, this is how you didn't pass.</div>
      {failureRows.map(([reason, count]) => {
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={reason} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: C.text }}>{reasonLabels[reason] || reason}</span>
              <span style={{ color: C.muted, fontFamily: "monospace" }}>{count} ({pct}%)</span>
            </div>
            <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: C.red }} />
            </div>
          </div>
        );
      })}
      {breakdown.totalIncomplete > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
            <span style={{ color: C.text }}>Incomplete (didn't hit target)</span>
            <span style={{ color: C.muted, fontFamily: "monospace" }}>
              {breakdown.totalIncomplete} ({Math.round((breakdown.totalIncomplete / total) * 100)}%)
            </span>
          </div>
          <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${Math.round((breakdown.totalIncomplete / total) * 100)}%`, height: "100%", background: C.muted }} />
          </div>
        </div>
      )}
    </Card>
  );
}

function ClosestCalls({ calls }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>Closest Calls</SectionTitle>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Days where you came within a hair of breaching a rule.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {calls.map((cc, i) => (
          <div key={i} style={{
            display: "grid", gridTemplateColumns: "90px 1fr 140px",
            gap: 12, alignItems: "center",
            padding: "8px 10px", background: "rgba(0,0,0,0.2)", borderRadius: 6, fontSize: 12,
          }}>
            <div style={{ color: C.yellow, fontFamily: "monospace", fontWeight: 600 }}>{cc.date}</div>
            <div style={{ color: C.sub }}>{cc.detail}</div>
            <div style={{ color: C.muted, fontSize: 10, textAlign: "right" }}>{cc.firm.firm_name} {cc.firm.plan_name}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Disclaimer() {
  return (
    <div style={{ marginTop: 24, padding: 14, background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
      <strong style={{ color: C.sub }}>How this works.</strong> For each preset, we replay your actual
      trade history under that firm's rules — daily losses, drawdowns, profit targets, consistency,
      minimum trading days — to determine whether the account would have survived. The "pass rate"
      is the fraction of simulated runs (starting from each available day in your history) that
      would have hit the profit target without blowing the account.
      <br /><br />
      <strong style={{ color: C.sub }}>Limits.</strong> Prop firms change rules often; verify against
      each firm's official site before trading live. This simulation does not account for unusual
      events like overnight holds, news restrictions, micro-scalping bans, or symbol restrictions.
      What-if scenarios and projections are illustrative only and are not financial advice.
    </div>
  );
}
